import { rem } from 'polished'
import { useCallback, useMemo } from 'react'
import { InView } from 'react-intersection-observer'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import MessageBanner from 'LuxKit/Banners/MessageBanner'
import TextButton from 'LuxKit/Button/TextButton'
import SolidStarIcon from 'LuxKit/Icons/solid/SolidStarIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import HeightFillingImage from 'components/Common/HeightFillingImage'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import { trackOfferPageSuggestedPackageViewPackageClick } from 'components/OfferPage/analytics'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { mediaQueryDown } from 'components/utils/breakpoint'
import { Experiments } from 'constants/experiments'
import { NO_IMAGE_AVAILABLE_ID } from 'constants/image'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useExperiment from 'hooks/Experiments/useExperiment'
import { getPopularForOccupantsBannerMessage } from 'hooks/OfferPage/useSuggestedPackageOptionFromTotalSales'
import { preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import { scrollToId } from 'lib/window/scrollUtils'
import type { CheapestRecommendedRoomAndRates } from 'selectors/offerPage/propertyOfferPageSelectors'
import {
  getCheapestRecommendedRoomAndRates,
  hasCheapestPromotionRate,
} from 'selectors/offerPage/propertyOfferPageSelectors'

import PropertyCancellationPolicy from './PropertyCancellationPolicy'
import PropertyCheapestRatePriceDetails from './PropertyCheapestRatePriceDetails'
import PropertyRateInclusions from './PropertyRateInclusions'

// NB: The styles below were stolen wholesale from `<SuggestedOptionPreview/>`.

const Root = styled.div`
  border: 1px solid ${props => props.theme.palette.highlight.primary.darkForeground};
  box-shadow: ${props => props.theme.shadow.bottom.small};
`

const ContentContainer = styled.div`
  flex: 1;
  container-type: inline-size;
`

const Content = styled.div`
  display: flex;
  padding: ${rem(20)};
  gap: ${rem(20)};
  flex-direction: column;

  @container (min-width: 410px) {
    flex-direction: row;
    align-items: stretch;
  }

  ${mediaQueryDown.mobile} {
    flex-direction: column;
    align-items: stretch;
  }
`

const DesktopImageContainer = styled.div`
  width: ${rem(182)};
  flex-shrink: 0;

  ${mediaQueryDown.mobile} {
    display: none;
  }
`

const MobileRoomImageWrap = styled.div`
  width: ${rem(64)};
  height: ${rem(64)};
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
`

const DetailsColumn = styled.div`
  flex: 1;
  min-width: 0;
`

const PricingColumn = styled(Group)`
  width: ${rem(170)};
  flex-shrink: 0;

  ${mediaQueryDown.mobile} {
    width: 100%;
    align-self: stretch;
  }
`

// NB: The styles above were stolen wholesale from `<SuggestedOptionPreview/>`.

interface Props {
  property: App.Property.Property
  occupancies: Array<OfferPageRoom>
  cheapestRecommendedRoomAndRates: CheapestRecommendedRoomAndRates
  isInApplicableExperimentVariation: boolean
  hasCheapestPromotionRate: boolean
}

function PropertyRecommendedRateSummary({
  property,
  occupancies,
  cheapestRecommendedRoomAndRates,
  isInApplicableExperimentVariation,
  hasCheapestPromotionRate,
}: Props) {
  const intl = useIntl()

  const strongCalloutMessage = useMemo(() => {
    return getPopularForOccupantsBannerMessage(intl, occupancies)
  }, [intl, occupancies])
  const scrollToRoom = useCallback(() => {
    const room = cheapestRecommendedRoomAndRates[0]?.[0]
    if (!room) return
    trackOfferPageSuggestedPackageViewPackageClick()
    scrollToId(room.id)
  }, [cheapestRecommendedRoomAndRates])

  const { triggerActivation } = useExperiment(Experiments.sppUpliftLpcRates, { canActivate: false })
  const onInView = useCallback((inView: boolean) => {
    if (inView) triggerActivation()
  }, [triggerActivation])
  // This shouldn't happen.
  if (!isInApplicableExperimentVariation) return null

  // Let `<PropertyPromotionSummary/>` render instead.
  if (hasCheapestPromotionRate) return null

  const [hotelOnlyRoomAndRate, hotelWithFlightsRoomAndRate] = cheapestRecommendedRoomAndRates
  if (!(hotelOnlyRoomAndRate && strongCalloutMessage)) return null

  const [room, hotelOnlyRate] = hotelOnlyRoomAndRate
  const roomImageId = room.images[0]?.id ?? NO_IMAGE_AVAILABLE_ID
  const hotelOnlyRateHeadlineCancellationPolicyStart = hotelOnlyRate.headlineCancellationPolicyStart
    ? new Date(hotelOnlyRate.headlineCancellationPolicyStart)
    : undefined
  const {
    inclusions,
    bonusInclusions,
    luxPlusInclusions,
    bundleInclusions,
  } = hotelOnlyRate
  const hasInclusions = !!(inclusions.length || bonusInclusions.length || luxPlusInclusions.length || bundleInclusions.length)
  // This is a quick heuristic to ensure the cheapest hotel-with-flights rate has the
  // same inclusions as the cheapest hotel-only rate.
  const hotelWithFlightsRate = hotelWithFlightsRoomAndRate?.[1].rateId === hotelOnlyRate.rateId
    ? hotelWithFlightsRoomAndRate[1]
    : undefined

  return <InView threshold={0.5} onChange={onInView}>
    <Root>
      <MessageBanner kind="promotional-primary" icon={<SolidStarIcon />}>{strongCalloutMessage}</MessageBanner>
      <Group direction="horizontal" verticalAlign="stretch">
        <DesktopImageContainer>
          <HeightFillingImage
            id={roomImageId}
            width={182}
            mobileWidth="182px"
            fit="center"
            quality="good"
            dpr={2}
          />
        </DesktopImageContainer>
        <ContentContainer>
          <Content>
            <DetailsColumn>
              <Group direction="vertical" gap={8}>
                <CSSBreakpoint max="mobile">
                  <Group direction="horizontal" gap={12} verticalAlign="center">
                    <MobileRoomImageWrap>
                      <ResponsiveImage
                        id={roomImageId}
                        width={64}
                        height={64}
                        mobileWidth="64px"
                        aspectRatio="1"
                        fit="center"
                        quality="good"
                        dpr={2}
                      />
                    </MobileRoomImageWrap>
                    <Heading variant="heading4">{room.name}</Heading>
                  </Group>
                </CSSBreakpoint>
                <CSSBreakpoint min="tablet">
                  <BodyText variant="large" weight="bold">{room.name}</BodyText>
                </CSSBreakpoint>
                <PropertyCancellationPolicy
                  propertyTimeZone={property.location.timezone}
                  headlineCancellationPolicyType={hotelOnlyRate.headlineCancellationPolicyType}
                  headlineCancellationPolicyStart={hotelOnlyRateHeadlineCancellationPolicyStart}
                  fullyRefundableUntil={hotelOnlyRate.fullyRefundableUntil}
                  cancellationPolicies={hotelOnlyRate.cancellationPolicies}
                  currency={hotelOnlyRate.currency}
                  shouldClarifyRefundIsHotelOnly={!!hotelWithFlightsRate}
                />
                {hasInclusions && <PropertyRateInclusions
                  property={property}
                  inclusions={inclusions}
                  bonusInclusions={bonusInclusions}
                  luxPlusInclusions={luxPlusInclusions}
                  bundleInclusions={bundleInclusions}
                  maxHeight={160}
                />}
              </Group>
            </DetailsColumn>
            <PricingColumn direction="vertical" gap={16} verticalAlign="end">
              <PropertyCheapestRatePriceDetails
                hotelOnlyRate={hotelOnlyRate}
                hotelWithFlightsRate={hotelWithFlightsRate}
                horizontalAlign="start"
              />
              <TextButton
                kind="primary"
                size="small"
                fit="mobile-full-width"
                onClick={scrollToRoom}
              >
                {intl.formatMessage({
                  id: '7z4-Ngi2aAhJS1Y3B-TV',
                  defaultMessage: 'View package',
                  description: 'Button text to view package details in suggested option preview',
                })}
              </TextButton>
            </PricingColumn>
          </Content>
        </ContentContainer>
      </Group>
    </Root>
  </InView>
}

type MappedProps = Props & { open: boolean }

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  property: state.property!, // Guaranteed by `<PropertyContainer/>`.
  occupancies: state.rooms,
  cheapestRecommendedRoomAndRates: getCheapestRecommendedRoomAndRates(state, appState),
  isInApplicableExperimentVariation: appState.optimizely.optimizelyExperiments[Experiments.sppUpliftLpcRates.id]?.variationKey === 'variant_b',
  hasCheapestPromotionRate: hasCheapestPromotionRate(state, appState),
  open: !!state.activeFilter,
}), PropertyRecommendedRateSummary, preventRenderWithOpenFilter)
